/**
 * Business Profile API Service
 * 
 * Provides clean interface for business profile operations
 * Can be used from campaigns, settings, and any other components
 */

import { createApiService, responseTransformers } from './apiUtils';

// Create business profiles API service
const businessProfilesApiService = createApiService('BusinessProfiles', '/api/campaigns/business-profiles');

export const businessProfileApi = {
  /**
   * Get simplified list of user's business profiles for UI components
   * Used in: Campaign creation, settings, profile selectors, etc.
   * @returns {Promise} Business profiles list optimized for UI
   */
  getProfilesForSelection: async () => {
    console.log('[Business Profile API] Fetching profiles for selection');

    const response = await businessProfilesApiService.get('/list_for_selection/');
    
    return responseTransformers.single(response, (data) => ({
      profiles: data.profiles || [],
      totalCount: data.total_count || 0,
      hasPrimary: data.has_primary || false
    }));
  },

  /**
   * Get all business profiles with full details
   * @returns {Promise} Complete business profiles list
   */
  getAllProfiles: async () => {
    console.log('[Business Profile API] Fetching all profiles');

    const response = await businessProfilesApiService.get('/');
    return responseTransformers.list(response);
  },

  /**
   * Get specific business profile by ID
   * @param {number} profileId - Profile ID
   * @returns {Promise} Business profile details
   */
  getProfile: async (profileId) => {
    console.log('[Business Profile API] Fetching profile:', profileId);

    const response = await businessProfilesApiService.get(`/${profileId}/`);
    return responseTransformers.single(response);
  },

  /**
   * Get user's primary business profile
   * @returns {Promise} Primary business profile
   */
  getPrimaryProfile: async () => {
    console.log('[Business Profile API] Fetching primary profile');

    const response = await businessProfilesApiService.get('/primary/');
    return responseTransformers.single(response);
  },

  /**
   * Create new business profile from URL
   * @param {Object} profileData - Profile creation data
   * @returns {Promise} Created business profile
   */
  createFromUrl: async (profileData) => {
    console.log('[Business Profile API] Creating profile from URL:', profileData);

    const response = await businessProfilesApiService.post('/create_from_url/', profileData);
    return responseTransformers.single(response);
  },

  /**
   * Create new business profile
   * @param {Object} profileData - Profile data
   * @returns {Promise} Created business profile
   */
  createProfile: async (profileData) => {
    console.log('[Business Profile API] Creating profile:', profileData);

    const response = await businessProfilesApiService.post('/', profileData);
    return responseTransformers.single(response);
  },

  /**
   * Update business profile
   * @param {number} profileId - Profile ID
   * @param {Object} profileData - Updated profile data
   * @returns {Promise} Updated business profile
   */
  updateProfile: async (profileId, profileData) => {
    console.log('[Business Profile API] Updating profile:', profileId, profileData);

    const response = await businessProfilesApiService.put(`/${profileId}/`, profileData);
    return responseTransformers.single(response);
  },

  /**
   * Set profile as primary
   * @param {number} profileId - Profile ID
   * @returns {Promise} Success response
   */
  setPrimary: async (profileId) => {
    console.log('[Business Profile API] Setting primary profile:', profileId);

    const response = await businessProfilesApiService.post(`/${profileId}/set_primary/`);
    return responseTransformers.single(response);
  },

  /**
   * Update brand assets for profile
   * @param {number} profileId - Profile ID
   * @param {Object} brandAssets - Brand assets data
   * @returns {Promise} Success response
   */
  updateBrandAssets: async (profileId, brandAssets) => {
    console.log('[Business Profile API] Updating brand assets:', profileId);

    const response = await businessProfilesApiService.post(`/${profileId}/update_brand_assets/`, {
      brand_assets: brandAssets
    });
    return responseTransformers.single(response);
  },

  /**
   * Update ICP analysis for profile
   * @param {number} profileId - Profile ID
   * @param {Object} icpAnalysis - ICP analysis data
   * @returns {Promise} Success response
   */
  updateIcpAnalysis: async (profileId, icpAnalysis) => {
    console.log('[Business Profile API] Updating ICP analysis:', profileId);

    const response = await businessProfilesApiService.post(`/${profileId}/update_icp_analysis/`, {
      icp_analysis: icpAnalysis
    });
    return responseTransformers.single(response);
  },

  /**
   * Refresh profile data from website
   * @param {number} profileId - Profile ID
   * @returns {Promise} Refresh response
   */
  refreshFromWebsite: async (profileId) => {
    console.log('[Business Profile API] Refreshing profile from website:', profileId);

    const response = await businessProfilesApiService.post(`/${profileId}/refresh_from_website/`);
    return responseTransformers.single(response);
  },

  /**
   * Delete (deactivate) business profile
   * @param {number} profileId - Profile ID
   * @returns {Promise} Success response
   */
  deleteProfile: async (profileId) => {
    console.log('[Business Profile API] Deleting profile:', profileId);

    const response = await businessProfilesApiService.delete(`/${profileId}/`);
    return { success: true };
  },

  /**
   * Get audience intelligence for profile
   * @param {number} profileId - Profile ID
   * @returns {Promise} Audience intelligence data
   */
  getAudienceIntelligence: async (profileId) => {
    console.log('[Business Profile API] Fetching audience intelligence:', profileId);

    const response = await businessProfilesApiService.get(`/${profileId}/audience-intelligence/`);
    return responseTransformers.single(response);
  },

  /**
   * Update audience intelligence for profile
   * @param {number} profileId - Profile ID
   * @param {Object} options - Update options
   * @returns {Promise} Updated audience intelligence
   */
  updateAudienceIntelligence: async (profileId, options = {}) => {
    console.log('[Business Profile API] Updating audience intelligence:', profileId, options);

    const response = await businessProfilesApiService.post(`/${profileId}/audience-intelligence/`, options);
    return responseTransformers.single(response);
  }
};

// Export individual functions for tree-shaking
export const {
  getProfilesForSelection,
  getAllProfiles,
  getProfile,
  getPrimaryProfile,
  createFromUrl,
  createProfile,
  updateProfile,
  setPrimary,
  updateBrandAssets,
  updateIcpAnalysis,
  refreshFromWebsite,
  deleteProfile,
  getAudienceIntelligence,
  updateAudienceIntelligence
} = businessProfileApi;

export default businessProfileApi; 